// =============================================================================
// PPIEnvios — o card da FILA de WhatsApp (placar + quem não recebeu + reenvio)
// =============================================================================
// Um só componente para os DOIS disparos do PPI, porque a pergunta do RH é a mesma
// nas duas telas: "todo mundo recebeu? quem não recebeu, por quê? dá pra reenviar?"
//   • Processos → grupo "inscricoes" (o aviso de inscrições abertas)
//   • Triagem   → grupo "triagem"    (convocação dos aprovados / "não foi dessa vez")
// As duas dividem a MESMA fila no banco; o `grupo` é o que separa os placares.
//
// ── O que mudou em 2026-07-14 ──
// 1) O placar separa "NÃO ENVIADO" de "COM ERRO". Não é preciosismo: são problemas
//    de dono diferente. "Não enviado" = o número está errado no CADASTRO (nem
//    tentamos mandar) e quem resolve é o RH, na origem. "Com erro" = tentamos e o
//    ManyChat recusou — aí o problema é lá. Jogar os dois no mesmo balde escondia
//    qual das duas coisas fazer.
// 2) O reenvio é SELETIVO: caixinha por pessoa.
//
// ── O que mudou em 2026-07-16 ──
// Quem JÁ RECEBEU passou a aparecer, em seção PRÓPRIA e recolhida, com o seu
// reenvio. Continua sendo mensagem duplicada e cobrada — o que mudou é que agora é
// uma ESCOLHA do RH, não uma impossibilidade. As duas listas são separadas porque
// as ações têm peso diferente: reenviar para quem não recebeu conserta uma falha;
// reenviar para quem recebeu gasta template de propósito. Por isso a seção de baixo
// tem aviso próprio e um confirm mais explícito.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Send, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, XCircle, Clock, RotateCcw, Zap, PhoneOff } from 'lucide-react';
import { CORES, BadgeStatus, Botao, estilos } from './ppiUI';

const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// Como cada classe se apresenta. A ordem aqui é a ordem de urgência para o RH.
const CLASSES = {
    naoEnviado: { rotulo: 'Não enviado', tom: 'alerta', ajuda: 'O número está errado ou ausente no cadastro — nem chegamos a tentar. Corrija na origem e reenvie.' },
    erro: { rotulo: 'Com erro', tom: 'reprovado', ajuda: 'Tentamos e o envio falhou.' },
    pendente: { rotulo: 'Na fila', tom: 'emProcesso', ajuda: 'Ainda vai ser enviado.' },
};

// `recarregarRef`: o pai guarda aqui a função de recarregar, para pedir uma
// atualização logo depois de publicar/disparar (a fila acabou de ganhar linhas).
function PPIEnvios({ idProcesso, grupo, titulo, recarregarRef }) {
    const [envios, setEnvios] = useState(null);
    const [verLista, setVerLista] = useState(false);
    const [marcados, setMarcados] = useState(() => new Set());
    // A seção "quem já recebeu" tem estado PRÓPRIO (lista e marcação): misturar as
    // marcações das duas faria o RH reenviar sem querer para quem já recebeu ao
    // clicar em "marcar todos" da lista de cima.
    const [verRecebidos, setVerRecebidos] = useState(false);
    const [marcadosRec, setMarcadosRec] = useState(() => new Set());
    const [drenando, setDrenando] = useState(false);
    const [reenviando, setReenviando] = useState(false);
    const [erro, setErro] = useState('');
    const [msg, setMsg] = useState('');

    const carregar = useCallback(async () => {
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${idProcesso}/envios?grupo=${grupo}`, { headers: authHeaders() });
            if (r.ok) setEnvios(await r.json());
        } catch (_) { /* silencioso: o card some, não quebra a tela */ }
    }, [idProcesso, grupo]);

    useEffect(() => { carregar(); }, [carregar]);
    useEffect(() => { if (recarregarRef) recarregarRef.current = carregar; }, [recarregarRef, carregar]);

    // Enquanto houver PENDENTE, o placar se atualiza sozinho: o worker está
    // drenando com freio de 1,5s por pessoa — o número anda devagar, de propósito.
    useEffect(() => {
        if (!envios || envios.pendentes <= 0) return undefined;
        const t = setInterval(carregar, 8000);
        return () => clearInterval(t);
    }, [envios, carregar]);

    const naoRecebidos = useMemo(() => envios?.naoRecebidos || [], [envios]);
    const recebidos = useMemo(() => envios?.recebidos || [], [envios]);

    // A fila é recarregada a cada 8s. Sem isto, uma pessoa que acabou de ser enviada
    // sairia da lista mas continuaria marcada — e o reenvio pediria por ela.
    // (Vale para as duas listas: quem é reenviado migra de uma para a outra.)
    const limparMortos = (setMarcacao, lista) => setMarcacao((atual) => {
        const vivos = new Set(lista.map((l) => l.ID));
        const limpo = new Set([...atual].filter((id) => vivos.has(id)));
        return limpo.size === atual.size ? atual : limpo;
    });
    useEffect(() => { limparMortos(setMarcados, naoRecebidos); }, [naoRecebidos]);
    useEffect(() => { limparMortos(setMarcadosRec, recebidos); }, [recebidos]);

    const alternador = (setMarcacao) => (id) => setMarcacao((s) => {
        const novo = new Set(s);
        if (novo.has(id)) novo.delete(id); else novo.add(id);
        return novo;
    });
    const alternar = alternador(setMarcados);
    const alternarRec = alternador(setMarcadosRec);

    const todosMarcados = naoRecebidos.length > 0 && marcados.size === naoRecebidos.length;
    const marcarTodos = () => setMarcados(todosMarcados ? new Set() : new Set(naoRecebidos.map((l) => l.ID)));
    const todosMarcadosRec = recebidos.length > 0 && marcadosRec.size === recebidos.length;
    const marcarTodosRec = () => setMarcadosRec(todosMarcadosRec ? new Set() : new Set(recebidos.map((l) => l.ID)));

    // O POST é o mesmo para as duas listas — o backend decide pelo `ids`. O que muda
    // é o CONFIRM: quem já recebeu vai receber de novo, e isso custa uma mensagem.
    const dispararReenvio = async (ids, limparMarcacao) => {
        setReenviando(true); setErro(''); setMsg('');
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${idProcesso}/envios/reenviar`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ grupo, ids }),
            });
            const d = await r.json();
            if (!r.ok) throw new Error(d.message || 'Erro ao reenviar.');
            setMsg(d.message);
            limparMarcacao(new Set());
            await carregar();
        } catch (e) { setErro(e.message); }
        finally { setReenviando(false); }
    };

    const reenviar = async () => {
        const ids = [...marcados];
        if (!ids.length) return;
        if (!window.confirm(
            `Enviar a mensagem para ${ids.length} pessoa(s)?\n\n`
            + `O telefone é relido do cadastro na hora do envio — se ele já foi corrigido, `
            + `a mensagem vai para o número novo.`
        )) return;
        await dispararReenvio(ids, setMarcados);
    };

    // Reenvio para quem JÁ RECEBEU. O confirm é mais duro de propósito: aqui não se
    // conserta falha nenhuma — manda-se a mesma mensagem outra vez, e cada uma é
    // cobrada. O RH precisa ler o número antes de clicar.
    //
    // ⚠️ O AVISO DO 131049 (2026-07-16) não é preciosismo — é o dano MAIOR.
    // O Meta tem um teto POR PESSOA de templates Marketing, adaptativo e somando
    // todas as empresas que mandam para aquele número. Estourado, ele simplesmente
    // NÃO ENTREGA ("131049 — healthy ecosystem engagement") e nada disso volta pra
    // nossa fila: a tela continua dizendo "Enviado". Aconteceu de verdade — um
    // número com 4 envios parou de receber enquanto outro, com 1, recebeu normal.
    // E o reflexo de quem vê "não chegou" é reenviar, que é exatamente o que piora.
    // Por isso o alerta cresce com o nº de envios de quem está marcado.
    const reenviarRecebidos = async () => {
        const ids = [...marcadosRec];
        if (!ids.length) return;

        // O maior nº de entregas entre os marcados. É o sinal de risco: quem já
        // recebeu 3-4 vezes é justamente quem o Meta está prestes a (ou já vai) cortar.
        const maxEnvios = Math.max(...recebidos.filter((e) => marcadosRec.has(e.ID)).map((e) => e.ENVIOS || 1));
        const alerta = maxEnvios >= 3
            ? `\n⚠️ Alguém da seleção já recebeu ${maxEnvios}× esta mensagem. O WhatsApp tem um limite `
              + `por pessoa e pode PARAR DE ENTREGAR sem avisar — e insistir piora. Se não chegou, `
              + `espere 24h em vez de reenviar de novo.\n`
            : '';

        if (!window.confirm(
            `Enviar a mensagem DE NOVO para ${ids.length} pessoa(s) que JÁ RECEBERAM?\n\n`
            + `Elas vão receber a mensagem repetida no WhatsApp, e cada envio é cobrado.\n`
            + alerta
            + `\nConfirma?`
        )) return;
        await dispararReenvio(ids, setMarcadosRec);
    };

    // Empurrão manual no worker. Em produção o cron drena a cada 5 min; no local os
    // crons ficam DESLIGADOS (NODE_ENV != production) e sem isto a fila não andaria.
    const drenarAgora = async () => {
        setDrenando(true);
        try {
            const r = await fetch('/api/treinamento/ppi/envios/drenar', { method: 'POST', headers: authHeaders() });
            if (!r.ok) throw new Error('Erro ao drenar a fila.');
            await carregar();
        } catch (e) { setErro(e.message); }
        finally { setDrenando(false); }
    };

    if (!envios || envios.total === 0) return null;   // ainda não disparou nada

    return (
        <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', margin: '24px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                <div>
                    <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>{titulo}</h4>
                    <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '2px 0 0 0' }}>
                        O envio é feito aos poucos (freio para não estourar o limite do WhatsApp) — o placar vai andando sozinho.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <Botao variant="secundario" icone={RotateCcw} onClick={carregar}>Atualizar</Botao>
                    {envios.pendentes > 0 && (
                        <Botao variant="secundario" icone={Zap} onClick={drenarAgora} disabled={drenando}>
                            {drenando ? 'Enviando…' : 'Enviar agora'}
                        </Botao>
                    )}
                </div>
            </div>

            {erro && <p style={{ color: '#be123c', fontSize: '13px', margin: '0 0 10px 0' }}>{erro}</p>}
            {msg && <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', margin: '0 0 10px 0' }}>{msg}</div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                {/* O nº de reenvios é o custo do envio deliberado — fica colado no
                    card de Enviados, que é onde o RH procura "quanto já mandamos". */}
                <Placar icone={CheckCircle2} cor="#059669" rotulo="Enviados" valor={envios.enviados} total={envios.total}
                    nota={envios.reenvios > 0 ? `+${envios.reenvios} reenvio(s)` : null} />
                <Placar icone={Clock} cor="#1d4ed8" rotulo="Na fila" valor={envios.pendentes} total={envios.total} />
                {/* NÃO ENVIADO: o número está errado no cadastro — nem tentamos mandar. */}
                <Placar icone={PhoneOff} cor="#b45309" rotulo="Não enviados" valor={envios.naoEnviados} total={envios.total} />
                <Placar icone={XCircle} cor="#e11d48" rotulo="Com erro" valor={envios.erros} total={envios.total} />
            </div>

            {envios.naoEnviados > 0 && (
                <div style={{ marginTop: '12px', backgroundColor: '#fffbeb', borderRadius: '10px', padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                    <p style={{ margin: 0, fontSize: '13px', color: '#b45309', lineHeight: 1.5 }}>
                        <strong>{envios.naoEnviados} pessoa(s) não receberam porque o celular está errado ou ausente no cadastro.</strong>{' '}
                        A mensagem nem foi tentada (não faz sentido gastar envio num número inválido). Corrija o número
                        na origem — a base recarrega todo dia — e depois <strong>marque a pessoa abaixo e reenvie</strong>:
                        o telefone é relido do cadastro na hora do envio.
                    </p>
                </div>
            )}

            {/* QUEM NÃO RECEBEU — com caixinha, para o reenvio seletivo. */}
            {naoRecebidos.length > 0 && (
                <div style={{ marginTop: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => setVerLista((v) => !v)} style={btnLink}>
                            {verLista
                                ? <>ocultar quem não recebeu <ChevronUp size={13} /></>
                                : <>ver os {naoRecebidos.length} que não receberam <ChevronDown size={13} /></>}
                        </button>
                        {verLista && (
                            <Botao icone={Send} onClick={reenviar} disabled={!marcados.size || reenviando} style={{ marginLeft: 'auto' }}>
                                {reenviando ? 'Enviando…' : `Enviar aos selecionados (${marcados.size})`}
                            </Botao>
                        )}
                    </div>

                    {verLista && (
                        <>
                            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '8px 0' }}>
                                Aqui está quem <strong>não recebeu</strong>. Quem já recebeu fica na seção abaixo —
                                reenviar para essas pessoas é mensagem duplicada, e o WhatsApp cobra por mensagem.
                            </p>
                            <div style={{ ...estilos.tabelaWrap, maxHeight: '320px', overflowY: 'auto' }}>
                                <table style={estilos.tabela}>
                                    <thead><tr>
                                        <th style={{ ...estilos.th, width: '36px' }}>
                                            <input type="checkbox" checked={todosMarcados} onChange={marcarTodos} title="Marcar todos" />
                                        </th>
                                        <th style={estilos.th}>Nome</th>
                                        <th style={estilos.th}>Telefone</th>
                                        <th style={estilos.th}>Situação</th>
                                        <th style={estilos.th}>Motivo</th>
                                        <th style={estilos.th}>Última tentativa</th>
                                    </tr></thead>
                                    <tbody>
                                        {naoRecebidos.map((e) => {
                                            const c = CLASSES[e.CLASSE] || CLASSES.pendente;
                                            return (
                                                <tr key={e.ID}>
                                                    <td style={estilos.td}>
                                                        <input type="checkbox" checked={marcados.has(e.ID)} onChange={() => alternar(e.ID)} />
                                                    </td>
                                                    <td style={estilos.td}>{e.NOME || '—'}</td>
                                                    <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{e.CELULAR || '—'}</td>
                                                    <td style={estilos.td}><BadgeStatus tom={c.tom}>{c.rotulo}</BadgeStatus></td>
                                                    <td style={{ ...estilos.td, fontSize: '12px', color: e.CLASSE === 'erro' ? '#be123c' : CORES.textoSec }}>
                                                        {e.ULTIMO_ERRO || (e.CLASSE === 'pendente' ? 'aguardando o envio' : '—')}
                                                    </td>
                                                    <td style={{ ...estilos.td, fontSize: '12px', color: CORES.textoFraco }}>{e.ULTIMA_TENTATIVA || '—'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* QUEM JÁ RECEBEU — o reenvio deliberado (2026-07-16).
                Recolhida por padrão: é a ação cara e a menos frequente. O RH abre
                quando quer reavisar alguém, não toda vez que confere o placar. */}
            {recebidos.length > 0 && (
                <div style={{ marginTop: '14px', borderTop: `1px solid ${CORES.borda}`, paddingTop: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => setVerRecebidos((v) => !v)} style={btnLink}>
                            {verRecebidos
                                ? <>ocultar quem já recebeu <ChevronUp size={13} /></>
                                : <>ver os {recebidos.length} que já receberam <ChevronDown size={13} /></>}
                        </button>
                        {verRecebidos && (
                            <Botao icone={Send} onClick={reenviarRecebidos} disabled={!marcadosRec.size || reenviando} style={{ marginLeft: 'auto' }}>
                                {reenviando ? 'Enviando…' : `Reenviar aos selecionados (${marcadosRec.size})`}
                            </Botao>
                        )}
                    </div>

                    {verRecebidos && (
                        <>
                            <div style={{ backgroundColor: '#fffbeb', borderRadius: '10px', padding: '12px 14px', margin: '10px 0', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                <AlertTriangle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <p style={{ margin: 0, fontSize: '13px', color: '#b45309', lineHeight: 1.5 }}>
                                    Estas pessoas <strong>já receberam a mensagem</strong>. Reenviar manda o
                                    WhatsApp <strong>de novo</strong> — mensagem repetida para elas e{' '}
                                    <strong>uma cobrança por envio</strong>. Marque só quem realmente precisa ser reavisado.
                                    <br />
                                    {/* O 131049: o teto do Meta é POR PESSOA e invisível para nós — a fila
                                        continua dizendo "Enviado". Quem vê "não chegou" tende a reenviar,
                                        que é justamente o que reforça o bloqueio. */}
                                    O WhatsApp também tem um <strong>limite por pessoa</strong>: quem recebe muitas
                                    mensagens seguidas <strong>para de receber</strong>, sem aviso e sem erro aqui na tela.
                                    A coluna <strong>Envios</strong> mostra quantas vezes cada um já recebeu —{' '}
                                    <strong>{'>'} 1 pede cautela</strong>. Se a mensagem não chegou, <strong>espere 24h</strong>;
                                    reenviar na hora só piora.
                                </p>
                            </div>
                            <div style={{ ...estilos.tabelaWrap, maxHeight: '320px', overflowY: 'auto' }}>
                                <table style={estilos.tabela}>
                                    <thead><tr>
                                        <th style={{ ...estilos.th, width: '36px' }}>
                                            <input type="checkbox" checked={todosMarcadosRec} onChange={marcarTodosRec} title="Marcar todos" />
                                        </th>
                                        <th style={estilos.th}>Nome</th>
                                        <th style={estilos.th}>Telefone</th>
                                        <th style={estilos.th}>1º envio</th>
                                        <th style={estilos.th}>Último envio</th>
                                        <th style={estilos.th}>Envios</th>
                                    </tr></thead>
                                    <tbody>
                                        {recebidos.map((e) => (
                                            <tr key={e.ID}>
                                                <td style={estilos.td}>
                                                    <input type="checkbox" checked={marcadosRec.has(e.ID)} onChange={() => alternarRec(e.ID)} />
                                                </td>
                                                <td style={estilos.td}>{e.NOME || '—'}</td>
                                                <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{e.CELULAR || '—'}</td>
                                                <td style={{ ...estilos.td, fontSize: '12px', color: CORES.textoFraco, fontVariantNumeric: 'tabular-nums' }}>{e.PRIMEIRO_ENVIO || '—'}</td>
                                                <td style={{ ...estilos.td, fontSize: '12px', color: CORES.textoFraco, fontVariantNumeric: 'tabular-nums' }}>{e.ULTIMO_ENVIO || '—'}</td>
                                                <td style={estilos.td}>
                                                    {/* >1 é o sinal de que já houve reenvio nesta pessoa. */}
                                                    {e.ENVIOS > 1
                                                        ? <BadgeStatus tom="alerta">{e.ENVIOS}×</BadgeStatus>
                                                        : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{e.ENVIOS || 1}×</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

const Placar = ({ icone: Icone, cor, rotulo, valor, total, nota }) => (
    <div style={{ backgroundColor: '#fff', border: `1px solid ${CORES.borda}`, borderLeft: `4px solid ${cor}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Icone size={20} color={cor} />
        <div>
            <div style={{ fontSize: '20px', fontWeight: 900, color: CORES.texto, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {valor}
                <span style={{ fontSize: '12px', fontWeight: 700, color: CORES.textoFraco }}> / {total}</span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: CORES.textoFraco, marginTop: '3px' }}>
                {rotulo}
                {nota && <span style={{ color: cor, marginLeft: '5px', fontVariantNumeric: 'tabular-nums' }}>{nota}</span>}
            </div>
        </div>
    </div>
);

const btnLink = { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: CORES.primaria, cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: 0 };

export default PPIEnvios;
